/**
 * Hisbah supplier-portal offer endpoints.
 * Backend: lumen/app/Http/Controllers/Partners/Portal/SupplierOfferController.
 *
 * SECURITY:
 *  - Auth header `token` must come from the active session (`useAuth`),
 *    NEVER from a request param or URL.
 *  - All endpoints are server-side scoped to PartnerPortalUser::supplier_id;
 *    the client cannot pass a different supplier_id even if it tries.
 *  - URL paths use the offer's product_supplier_id; the server validates
 *    ownership before responding (cross-supplier reads return 404).
 */

import axios from 'axios'
import {API_URL} from '../../../tenant/apiBase'
import {branchScopeParams} from '../../common/actingBranch'
import {
  IBarcodeLookupResult,
  IFromBarcodePayload,
  IOfferListFilters,
  IOfferListResponse,
  IOfferModel,
  IOfferPerformance,
  IOfferSummary,
  IOfferUpdatePayload,
  IVariantRequestPayload,
  IWhyNotWinning,
} from './_models'

const OFFERS_BASE = `${API_URL}/api/partner-portal/offers`

/** Build the standard request headers. Token is required for every call. */
function authHeaders(token: string | undefined, language?: string) {
  return {
    token: token || '',
    language: language || 'en',
  }
}

/** Serialise the filter object as URLSearchParams (skipping empties). */
function buildListQuery(filters: IOfferListFilters): string {
  const qs = new URLSearchParams()
  if (filters.page && filters.page > 0) qs.set('page', String(filters.page))
  if (filters.limit && filters.limit > 0) qs.set('limit', String(filters.limit))
  if (filters.status && filters.status !== 'all') qs.set('status', filters.status)
  if (filters.winning_only) qs.set('winning_only', '1')
  if (filters.not_winning) qs.set('not_winning', '1')
  const term = (filters.search ?? '').trim()
  if (term !== '') qs.set('search', term)
  if (filters.sort) qs.set('sort', filters.sort)
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function getOffers(
  filters: IOfferListFilters,
  token: string | undefined,
  language?: string,
) {
  return axios.get<IOfferListResponse>(`${OFFERS_BASE}${buildListQuery(filters)}`, {
    headers: authHeaders(token, language),
  })
}

export function getOfferSummary(token: string | undefined, language?: string) {
  return axios.get<{data: IOfferSummary}>(`${OFFERS_BASE}/summary`, {
    headers: authHeaders(token, language),
  })
}

export function getOfferPerformance(token: string | undefined, language?: string) {
  return axios.get<{data: IOfferPerformance}>(`${OFFERS_BASE}/performance`, {
    headers: authHeaders(token, language),
  })
}

export function getOffer(
  productSupplierId: number,
  token: string | undefined,
  language?: string,
) {
  return axios.get<{data: IOfferModel}>(`${OFFERS_BASE}/${productSupplierId}`, {
    headers: authHeaders(token, language),
  })
}

export function updateOffer(
  productSupplierId: number,
  payload: IOfferUpdatePayload,
  token: string | undefined,
  language?: string,
  allBranches?: boolean,
) {
  return axios.put<{data: IOfferModel}>(`${OFFERS_BASE}/${productSupplierId}`, payload, {
    headers: authHeaders(token, language),
    params: branchScopeParams(allBranches),
  })
}

export function pauseOffer(
  productSupplierId: number,
  token: string | undefined,
  language?: string,
) {
  return axios.post<{data: IOfferModel}>(
    `${OFFERS_BASE}/${productSupplierId}/pause`,
    {},
    {headers: authHeaders(token, language)},
  )
}

export function activateOffer(
  productSupplierId: number,
  token: string | undefined,
  language?: string,
) {
  return axios.post<{data: IOfferModel}>(
    `${OFFERS_BASE}/${productSupplierId}/activate`,
    {},
    {headers: authHeaders(token, language)},
  )
}

/**
 * Anonymized buy-box explainer for a non-winning offer. Returns the winner's
 * price/delivery metrics (no identity), reason codes, and a price_to_win
 * suggestion. Server validates ownership (404 if not the supplier's offer).
 */
export function getWhyNotWinning(
  productSupplierId: number,
  token: string | undefined,
  language?: string,
) {
  return axios.get<{data: IWhyNotWinning}>(
    `${OFFERS_BASE}/${productSupplierId}/why-not-winning`,
    {headers: authHeaders(token, language)},
  )
}

/**
 * Tri-state barcode lookup. The Add-Product wizard calls this before
 * the supplier fills any other field; the response decides whether to
 * proceed with the full wizard, switch to the match-offer flow, or
 * redirect them to their existing offer.
 *
 * The endpoint returns 200 in all three states — the `state` field on
 * the response body is the discriminator. Network/server errors throw.
 */
export function lookupBarcode(
  barcode: string,
  token: string | undefined,
  language?: string,
) {
  const qs = new URLSearchParams({barcode: barcode.trim()}).toString()
  return axios.get<IBarcodeLookupResult>(`${OFFERS_BASE}/lookup-barcode?${qs}`, {
    headers: authHeaders(token, language),
  })
}

/**
 * "Sell Yours" — create an offer for an existing catalog product, by
 * barcode. Server resolves the barcode to product_id and scopes the
 * new offer to the authed supplier.
 *
 * 201 on success, 409 if the supplier already has an offer for this
 * product (response carries `product_supplier_id` so the UI can
 * deep-link to edit instead).
 */
export function createOfferFromBarcode(
  payload: IFromBarcodePayload,
  token: string | undefined,
  language?: string,
) {
  return axios.post<{data: IOfferModel}>(`${OFFERS_BASE}/from-barcode`, payload, {
    headers: authHeaders(token, language),
  })
}

/**
 * "Add a variant on a used barcode" — file a product request for a
 * size/color not yet in the catalog, under an existing barcode family.
 * The server derives the master and copies its catalog content; the offer
 * materializes when the catalog team approves the request.
 *
 * 201 with `{ state: 'pending_request', request_id }` on success.
 * 404 if the barcode isn't in the catalog; 409 if the variant already
 * exists (use createOfferFromBarcode instead) or a request is pending.
 */
export function requestVariantFromBarcode(
  payload: IVariantRequestPayload,
  token: string | undefined,
  language?: string,
) {
  const {images, ...fields} = payload
  // New-COLOR variants can carry photos → send multipart so the files ride
  // along (server reads product_image[]). Size-only variants (no images) keep
  // the plain JSON post.
  if (images && images.length > 0) {
    const fd = new FormData()
    Object.entries(fields).forEach(([key, value]) => {
      if (value !== undefined && value !== null) fd.append(key, String(value))
    })
    images.forEach((file) => fd.append('product_image[]', file))
    return axios.post<{state: string; request_id: number}>(
      `${OFFERS_BASE}/request-variant`,
      fd,
      {headers: authHeaders(token, language)},
    )
  }
  return axios.post<{state: string; request_id: number}>(
    `${OFFERS_BASE}/request-variant`,
    fields,
    {headers: authHeaders(token, language)},
  )
}

// Catalog-edit requests now go through the standard
// /api/partner-portal/product/edit-product endpoint (used by the
// product-edit form), not a per-offer suggestion endpoint. The
// suggest-catalog-change endpoints were rolled back when the team
// chose Option A: one queue, one UX, the full product form.
